import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { db } from '../firebase/config';
import { 
    collection, 
    addDoc, 
    getDocs,
    query,
    orderBy,
    doc,
    getDoc,
    serverTimestamp 
} from 'firebase/firestore';
import './Uniform.css';

const Uniform = () => {
    const { currentUser } = useAuth();
    const { showToast } = useToast();
    const [uniforms, setUniforms] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showForm, setShowForm] = useState(false);
    const [isUniformLeader, setIsUniformLeader] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [formData, setFormData] = useState({
        uniformName: '',
        price: '',
        description: '',
        imageUrl: ''
    });

    useEffect(() => {
        checkUniformLeader();
        loadUniforms();
    }, [currentUser]);

    const checkUniformLeader = async () => {
        if (!currentUser) {
            setIsUniformLeader(false);
            return;
        }

        try {
            const adminDoc = await getDoc(doc(db, "admins", currentUser.uid));
            if (adminDoc.exists()) {
                const adminData = adminDoc.data();
                setIsUniformLeader(adminData.isDepartmentLeader === "uniform");
            } else {
                setIsUniformLeader(false);
            }
        } catch (error) {
            console.error("Error checking uniform leader status:", error);
            setIsUniformLeader(false);
        }
    };

    const loadUniforms = async () => {
        setLoading(true);
        try {
            const uniformsQuery = query(
                collection(db, "uniforms"),
                orderBy("createdAt", "desc")
            );
            
            const querySnapshot = await getDocs(uniformsQuery);
            const uniformsData = [];
            
            querySnapshot.forEach((doc) => {
                uniformsData.push({ id: doc.id, ...doc.data() });
            });
            
            setUniforms(uniformsData);
        } catch (error) {
            console.error("Error loading uniforms:", error);
            showToast('Error loading uniforms', 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleInputChange = (e) => {
        const { id, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [id]: value
        }));
    };

    const resetForm = () => {
        setFormData({
            uniformName: '',
            price: '',
            description: '',
            imageUrl: ''
        });
    };

    const validateForm = () => {
        if (!formData.uniformName.trim()) {
            showToast('Please enter a uniform name', 'error');
            return false;
        }

        const price = parseFloat(formData.price);
        if (isNaN(price) || price <= 0) {
            showToast('Please enter a valid price', 'error');
            return false;
        }

        if (!formData.description.trim()) {
            showToast('Please enter a description', 'error');
            return false;
        }

        if (!formData.imageUrl.trim()) {
            showToast('Please enter a Google Drive link', 'error');
            return false;
        }

        if (!formData.imageUrl.includes('drive.google.com')) {
            showToast('Please provide a valid Google Drive link', 'error');
            return false;
        }

        return true;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!currentUser) {
            showToast('Please login to add uniforms', 'error');
            return;
        }

        if (!isUniformLeader) {
            showToast('Only Uniform Department Leaders can add new uniforms', 'error');
            return;
        }

        if (!validateForm()) {
            return;
        }

        setSubmitting(true);

        try {
            const uniformData = {
                uniformName: formData.uniformName.trim(),
                price: parseFloat(formData.price),
                description: formData.description.trim(),
                imageUrl: formData.imageUrl.trim(),
                createdAt: serverTimestamp(),
                addedBy: currentUser.uid,
                addedByEmail: currentUser.email
            };

            await addDoc(collection(db, "uniforms"), uniformData);

            showToast('Uniform design added successfully!', 'success');
            resetForm();
            setShowForm(false);
            loadUniforms(); // Reload uniforms
        } catch (error) {
            console.error("Error adding uniform:", error);
            showToast('Failed to add uniform', 'error');
        } finally {
            setSubmitting(false);
        }
    };

    const formatPrice = (price) => {
        return new Intl.NumberFormat('en-KE', {
            style: 'currency',
            currency: 'KES',
            minimumFractionDigits: 0
        }).format(price);
    };

    const extractDriveFileId = (url) => {
        // Extract file ID from Google Drive URL
        const match = url.match(/\/d\/(.*?)\/|file\/d\/(.*?)\/|id=(.*?)(&|$)/);
        if (match) {
            return match[1] || match[2] || match[3];
        }
        return null;
    };

    const getDriveEmbedUrl = (url) => {
        const fileId = extractDriveFileId(url);
        if (fileId) {
            return `https://drive.google.com/file/d/${fileId}/preview`;
        }
        return url;
    };

    if (!currentUser) {
        return (
            <div className="uniform-page">
               

                <div className="uniform-main-content">
                    <h1 className="uniform-page-title">Ministry Uniform Designs</h1>
                    
                    <div className="uniform-login-prompt">
                        <i className="fas fa-user-circle"></i>
                        <h3>Please Login to View Uniforms</h3>
                        <p>You need to be logged in to access the ministry uniform designs.</p>
                        <button 
                            className="uniform-login-btn"
                            onClick={() => window.location.href = '/login'}
                        >
                            <i className="fas fa-sign-in-alt"></i> Go to Login
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="uniform-page">
            
            <div className="uniform-main-content">
                <h1 className="uniform-page-title">Ministry Uniform Designs</h1>

                {/* Message Area */}
                <div id="uniform-message" className="uniform-message"></div>

                {/* Loading State */}
                {loading && (
                    <div className="uniform-loading">
                        <div className="uniform-loading-spinner"></div>
                        <p>Loading uniforms...</p>
                    </div>
                )}

                {/* Uniform Cards Grid */}
                {!loading && (
                    <div className="uniforms-grid">
                        {uniforms.length === 0 ? (
                            <div className="uniform-no-data">
                                <i className="fas fa-tshirt"></i>
                                <h3>No Uniform Designs Added Yet</h3>
                                <p>Check back later for new uniform designs</p>
                            </div>
                        ) : (
                            uniforms.map((uniform) => (
                                <div key={uniform.id} className="uniform-card">
                                    <h3 className="uniform-card-title">{uniform.uniformName}</h3>
                                    <div className="uniform-card-price">
                                        {formatPrice(uniform.price)}
                                    </div>
                                    <p className="uniform-card-description">
                                        {uniform.description}
                                    </p>
                                    <div className="uniform-card-actions">
                                        <a 
                                            href={uniform.imageUrl} 
                                            target="_blank" 
                                            rel="noopener noreferrer"
                                            className="uniform-view-link"
                                        >
                                            <i className="fas fa-external-link-alt"></i>
                                            View Design
                                        </a>
                                        {getDriveEmbedUrl(uniform.imageUrl) !== uniform.imageUrl && (
                                            <button
                                                className="uniform-preview-btn"
                                                onClick={() => {
                                                    window.open(getDriveEmbedUrl(uniform.imageUrl), '_blank');
                                                }}
                                            >
                                                <i className="fas fa-eye"></i>
                                                Preview
                                            </button>
                                        )}
                                    </div>
                                </div>
                            ))
                        )}
                    </div>
                )}

                {/* Add New Uniform Form */}
                {showForm && isUniformLeader && (
                    <div className="uniform-form-container">
                        <div className="uniform-form-header">
                            <h2 className="uniform-form-title">Add New Uniform Design</h2>
                            <button 
                                className="uniform-close-form"
                                onClick={() => setShowForm(false)}
                            >
                                &times;
                            </button>
                        </div>

                        <form onSubmit={handleSubmit} className="uniform-form">
                            <div className="uniform-form-group">
                                <label htmlFor="uniformName">Uniform Name*</label>
                                <input
                                    type="text"
                                    id="uniformName"
                                    className="uniform-form-control"
                                    placeholder="e.g. Doves @30, Doves Concert Uniform"
                                    value={formData.uniformName}
                                    onChange={handleInputChange}
                                    required
                                />
                            </div>

                            <div className="uniform-form-group">
                                <label htmlFor="price">Price (KES)*</label>
                                <input
                                    type="number"
                                    id="price"
                                    className="uniform-form-control"
                                    placeholder="2500"
                                    min="0"
                                    step="0.01"
                                    value={formData.price}
                                    onChange={handleInputChange}
                                    required
                                />
                            </div>

                            <div className="uniform-form-group">
                                <label htmlFor="description">Description*</label>
                                <textarea
                                    id="description"
                                    className="uniform-form-control"
                                    placeholder="Describe the uniform, materials, sizes available, etc."
                                    rows="4"
                                    value={formData.description}
                                    onChange={handleInputChange}
                                    required
                                ></textarea>
                            </div>

                            <div className="uniform-form-group">
                                <label htmlFor="imageUrl">Google Drive Link*</label>
                                <input
                                    type="url"
                                    id="imageUrl"
                                    className="uniform-form-control"
                                    placeholder="https://drive.google.com/file/d/XXXXX/view?usp=sharing"
                                    value={formData.imageUrl}
                                    onChange={handleInputChange}
                                    required
                                />
                                <small className="uniform-hint">
                                    Ensure the link is shared with "Anyone with the link" permission
                                </small>
                            </div>

                            <button 
                                type="submit" 
                                className="uniform-submit-btn"
                                disabled={submitting}
                            >
                                {submitting ? (
                                    <>
                                        <i className="fas fa-spinner fa-spin"></i>
                                        Adding...
                                    </>
                                ) : (
                                    <>
                                        <i className="fas fa-plus-circle"></i>
                                        Add Uniform Design
                                    </>
                                )}
                            </button>
                        </form>
                    </div>
                )}
            </div>

            {/* Floating Action Button */}
            {isUniformLeader && (
                <button 
                    className="uniform-fab"
                    onClick={() => setShowForm(!showForm)}
                    title={showForm ? "Close Form" : "Add Uniform"}
                >
                    <i className={`fas fa-${showForm ? 'times' : 'plus'}`}></i>
                </button>
            )}
        </div>
    );
};

export default Uniform;
